import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import * as React from 'react';
import { useControlledValue } from "../../hooks/_private/useControlledValue";
import { useElementId } from "../../hooks/useElementId";
import { classNames } from "../../utils/helpers";
import { AccordionTrigger } from './AccordionTrigger';
import { AccordionContent } from './AccordionContent';
import { AccordionContext } from './Accordion.context';
import { AccordionGroupContext } from './AccordionGroup.context';
import { View } from "../view";
import s from './Accordion.module.css';
export const Accordion = ({ attributes, contentAttributes, children, className, defaultOpen, disabled, headerOptions, name, open, variant, onToggle, }) => {
    const { disabled: groupDisabled, openAccordionNames, variant: groupVariant, onGroupChange, } = React.useContext(AccordionGroupContext);
    const id = useElementId();
    const [controlledOpen, setControlledOpen] = useControlledValue({
        defaultValue: defaultOpen,
        value: open,
        onValueChange: onToggle,
    }, false);
    // Various checks when using AccordionGroup
    if (openAccordionNames) {
        if (open !== undefined) {
            // eslint-disable-next-line no-console
            console.warn("The AccordionGroup component will only properly take effect with uncontrolled Accordion components. The 'open' prop must be omitted for an Accordion to be uncontrolled.");
        }
        if (defaultOpen) {
            // eslint-disable-next-line no-console
            console.warn("The 'defaultOpen' prop set on the Accordion component will not take effect while a child of an AccordionGroup. You can either set the initial state for it to be open when using a controlled AccordionGroup or use the uncontrolled AccordionGroup's 'defaultOpenItems' prop.");
        }
        if (groupDisabled && disabled === false) {
            // eslint-disable-next-line no-console
            console.warn("The 'disabled' prop set on the Accordion component will not take effect because the 'disabled' prop of the AccordionGroup will override it when set to 'true'.");
        }
        if (!name) {
            // eslint-disable-next-line no-console
            console.warn("When using AccordionGroup, it is highly recommended to supply 'name' props to the individual Accordion components or unexpected behavior may occur.");
        }
        if (variant) {
            // eslint-disable-next-line no-console
            console.warn("The 'variant' prop set on the Accordion component will not take effect because the 'variant' prop of the AccordionGroup will override it.");
        }
    }
    const reconciledDisabled = groupDisabled || disabled;
    const reconciledId = name || id;
    const reconciledVariant = groupVariant || variant || 'list';
    const reconciledOnToggle = (updatedOpen) => {
        if (openAccordionNames) {
            if (updatedOpen) {
                onGroupChange === null || onGroupChange === void 0 ? void 0 : onGroupChange([...openAccordionNames, reconciledId]);
            }
            else {
                onGroupChange === null || onGroupChange === void 0 ? void 0 : onGroupChange(openAccordionNames.filter(openAccordionName => openAccordionName !== reconciledId));
            }
        }
        setControlledOpen(updatedOpen);
    };
    const rootClassNames = classNames(s.root, s[`--variant-${reconciledVariant}`], reconciledDisabled && s['--disabled'], className);
    const value = React.useMemo(() => ({
        contentId: `${reconciledId}-content`,
        disabled: reconciledDisabled,
        open: openAccordionNames ? openAccordionNames.includes(reconciledId) : controlledOpen,
        triggerId: `${reconciledId}-trigger`,
        variant: reconciledVariant,
        onToggle: reconciledOnToggle,
    }), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [controlledOpen, openAccordionNames]);
    const canFocusContent = open || (openAccordionNames === null || openAccordionNames === void 0 ? void 0 : openAccordionNames.includes(reconciledId)) || value.open === true ? 0 : -1;
    return (_jsx("div", Object.assign({}, attributes, { className: rootClassNames, "data-testid": "st-accordion", children: _jsxs(AccordionContext.Provider, { value: value, children: [_jsx(AccordionTrigger, Object.assign({}, headerOptions)), _jsx(AccordionContent, { children: _jsx(View, { attributes: { tabIndex: (contentAttributes === null || contentAttributes === void 0 ? void 0 : contentAttributes.tabIndex) || canFocusContent }, children: children }) })] }) })));
};
